import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

// Variable global para almacenar el token dinámico para operaciones de delivery
let currentDeliveryAuthToken: string | null = null;

// Función para actualizar el token dinámicamente
export const setDeliveryAuthToken = (token: string | null) => {
    currentDeliveryAuthToken = token;
};

// Función para obtener el token actual
export const getDeliveryAuthToken = (): string | null => {
    return currentDeliveryAuthToken;
};

// URL del BFF de delivery desde variable de entorno
let deliveryGraphqlUrl = import.meta.env.VITE_GRAPHQL_DELIVERY;

if (!deliveryGraphqlUrl) {
    throw new Error('❌ VITE_GRAPHQL_DELIVERY no está configurado en las variables de entorno');
}

// Asegurar que tenga protocolo https://
if (!deliveryGraphqlUrl.startsWith('http://') && !deliveryGraphqlUrl.startsWith('https://')) {
    deliveryGraphqlUrl = 'https://' + deliveryGraphqlUrl;
}

// Siempre agregar /query si no lo tiene
if (!deliveryGraphqlUrl.endsWith('/query')) {
    deliveryGraphqlUrl = deliveryGraphqlUrl.replace(/\/$/, '') + '/query';
}

// Enlace HTTP para operaciones de delivery
const deliveryHttpLink = createHttpLink({
    uri: deliveryGraphqlUrl,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    fetch: (uri, options) => {
        return fetch(uri, options).then(response => {
            return response;
        }).catch(error => {
            console.error('❌ [apollo-delivery] Fetch error:', error);
            throw error;
        });
    },
});

// Enlace de contexto para añadir el token JWT en los headers
const deliveryAuthLink = setContext(async (_, { headers }) => {
    let token = currentDeliveryAuthToken;

    // Si aún no hay token, esperar a que el frontend padre lo envíe (evita race condition 401)
    if (!token && typeof window !== 'undefined' && (window as any).microfrontendAuth?.waitForToken) {
        try {
            token = await (window as any).microfrontendAuth.waitForToken(5000);
            if (token) currentDeliveryAuthToken = token;
        } catch {
            // Timeout: proceder sin token
        }
    }

    return {
        headers: {
            ...headers,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            authorization: token ? `Bearer ${token}` : '',
        }
    };
});

// Enlace de manejo de errores
const deliveryErrorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.error(
                `[GraphQL Delivery Error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
            
            // Detectar errores de autenticación
            if (
                message?.toLowerCase().includes('unauthorized') ||
                message?.toLowerCase().includes('authentication') ||
                message?.toLowerCase().includes('jwt')
            ) {
                const hadToken = !!currentDeliveryAuthToken;
                console.error('❌ [Apollo Delivery] Token expirado o inválido.');
                currentDeliveryAuthToken = null;
                
                // Solo notificar al padre si teníamos un token válido (no por race condition)
                if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                    (window as any).microfrontendAuth.notifyTokenExpired();
                }
            }
        });
    }

    if (networkError) {
        const statusCode = (networkError as any)?.statusCode;
        if (statusCode === 401 || statusCode === 403) {
            const hadToken = !!currentDeliveryAuthToken;
            console.error('❌ [Apollo Delivery] Error de autenticación (HTTP', statusCode, ')');
            currentDeliveryAuthToken = null;
            
            // Solo notificar al padre si teníamos un token válido (no por race condition)
            if (hadToken && typeof window !== 'undefined' && (window as any).microfrontendAuth) {
                (window as any).microfrontendAuth.notifyTokenExpired();
            }
        }
    }
});


console.log('🔌 [apollo-delivery] HTTP URL:', deliveryGraphqlUrl);

// Cliente Apollo para operaciones de delivery (sin WebSocket - usa HTTP multipart para subscriptions)
const deliveryApolloClient = new ApolloClient({
    link: deliveryErrorLink.concat(deliveryAuthLink.concat(deliveryHttpLink)),
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        },
        mutate: {
            errorPolicy: 'all',
        },
    },
});

export default deliveryApolloClient;
